import { Box, Tooltip, Typography } from '@mui/material';
import type { ProgressNoteDue } from 'src/types/progressNoteDue';
import {
  plannerProgressNoteDueTitle,
  progressNoteDueStatus,
  schedulerProgressNoteDueTitle,
} from 'src/utils/progressNoteDue';

// THIN-8141 (design THIN-8146): the progress-note due indicators. Purple = due today, red = overdue,
// indigo = upcoming on the planner. Due today is a neutral purple so it reads apart from overdue (THIN-8192).
// Kept distinct from the orange supervision triangle and the red task-overdue circle.
const BRAND_DEEP = '#4338CA';
const PURPLE_INK = '#6941C6';
const PURPLE_BG = '#F4EBFF';
const RED_INK = '#B42318';
const RED_BG = '#FDECEA';

const NoteIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="2.5"
      y="1.8"
      width="11"
      height="12.4"
      rx="1.6"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M5.4 5.6h5.2M5.4 8h5.2M5.4 10.4h3"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

type SchedulerProps = {
  due?: ProgressNoteDue | null;
  /** The scheduler's selected date, YYYY-MM-DD. */
  date: string;
};

/** Scheduler: shown only when the note is due on the selected date or already overdue. */
const isShownOnScheduler = (due: ProgressNoteDue | null | undefined, date: string) => {
  const status = progressNoteDueStatus(due, date);
  return status === 'DUE_TODAY' || status === 'OVERDUE';
};

export const SchedulerProgressNoteDueGlyph = ({ due, date }: SchedulerProps) => {
  if (!due || !isShownOnScheduler(due, date)) return null;
  const overdue = progressNoteDueStatus(due, date) === 'OVERDUE';
  const ink = overdue ? RED_INK : PURPLE_INK;
  return (
    <Tooltip
      title={schedulerProgressNoteDueTitle(due, date)}
      placement="top"
    >
      <Box
        data-testid="progress-note-due-glyph"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          borderRadius: '5px',
          padding: '3px 6px',
          color: ink,
          backgroundColor: overdue ? RED_BG : PURPLE_BG,
        }}
      >
        <NoteIcon />
        <Typography sx={{ fontSize: '9px', fontWeight: 700, color: ink, lineHeight: 1 }}>
          PN
        </Typography>
      </Box>
    </Tooltip>
  );
};

export const SchedulerProgressNoteDueDot = ({ due, date }: SchedulerProps) => {
  if (!due || !isShownOnScheduler(due, date)) return null;
  const overdue = progressNoteDueStatus(due, date) === 'OVERDUE';
  return (
    <Tooltip
      title={schedulerProgressNoteDueTitle(due, date)}
      placement="top"
    >
      <Box
        data-testid="progress-note-due-dot"
        sx={{
          width: '7px',
          height: '7px',
          borderRadius: '100px',
          flexShrink: 0,
          mr: '6px',
          backgroundColor: overdue ? RED_INK : PURPLE_INK,
        }}
      />
    </Tooltip>
  );
};

type PlannerProps = {
  due: ProgressNoteDue;
  /** Today, YYYY-MM-DD. */
  today: string;
};

/**
 * Planner: a tab hanging from the top edge of the due-date cell. Dashed when the date rests on
 * planned visits (it can still move), solid when settled.
 */
export const PlannerProgressNoteDueTab = ({ due, today }: PlannerProps) => {
  const status = progressNoteDueStatus(due, today);
  const ink = status === 'OVERDUE' ? RED_INK : status === 'DUE_TODAY' ? PURPLE_INK : BRAND_DEEP;
  const border = status === 'OVERDUE' ? '#E8A79F' : status === 'DUE_TODAY' ? '#D6BBFB' : '#C7C2F0';
  return (
    <Tooltip
      placement="top"
      title={
        <Box>
          {plannerProgressNoteDueTitle(due, today).map((line) => (
            <Typography
              key={line}
              sx={{ fontSize: '12px' }}
            >
              {line}
            </Typography>
          ))}
        </Box>
      }
    >
      <Box
        data-testid="progress-note-due-tab"
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 6,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          backgroundColor: 'white',
          // Hangs from the cell's top edge, so no top border.
          borderWidth: '0 1px 1px',
          borderStyle: due.projected ? 'dashed' : 'solid',
          borderColor: border,
          borderRadius: '0 0 7px 7px',
          padding: '2px 8px 3px',
          color: ink,
          whiteSpace: 'nowrap',
        }}
      >
        <NoteIcon size={11} />
        <Typography
          sx={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.3px', lineHeight: 1 }}
        >
          PN
        </Typography>
      </Box>
    </Tooltip>
  );
};
